import { type Edge } from '@xyflow/react'
import {
  type TChatbotNode,
  type TNodeAction,
  type TNodeRequestHumanReview,
  type TNodeVariable,
  type TRange,
} from './node.type'
import { EVariableType, type TVariable } from 'src/types/variable'
import { type TPatientLocale } from 'src/types/locales'
import {
  type ConversationAction,
  type ConversationConnection,
  type ConversationHumanReviewCheck,
  type ConversationNodes,
} from '../../_components/chatbot-general-form/chatbot-api.type'
import {
  EHumanReviewRequestProblem,
  isHumanReviewRequestProblem,
} from 'src/pages/dashboard/_components/human-review-requests.type'
import {
  DEFAULT_EDGE_TYPE,
  MEDIA_NOT_UPLOADED_HANDLE,
  MEDIA_UPLOADED_HANDLE,
  START_NODE_ID,
} from './chatbot.constants'

function _buildHumanReviewChecksForApi(humanReviewCheck?: TNodeRequestHumanReview) {
  if (!humanReviewCheck) return undefined
  return [{ problem: EHumanReviewRequestProblem.MISSING_DOCUMENT as const, delay: humanReviewCheck.delay }]
}

function _extractHumanReviewCheckFromApi(
  humanReviewChecks?: ConversationHumanReviewCheck[],
): TNodeRequestHumanReview | undefined {
  const documentCheck = humanReviewChecks?.find(
    (check) => check.problem === EHumanReviewRequestProblem.MISSING_DOCUMENT,
  )
  return documentCheck && { delay: documentCheck.delay }
}

export function convertNodesToApiFormat(nodes: TChatbotNode[], edges: Edge[]): ConversationNodes {
  const conversationNodes: ConversationNodes = {}
  let needsTerminalNode = false

  nodes.forEach((node) => {
    const { title, messagesByLanguage, variable, clarificationMessage, humanReviewCheck } = node.data

    const actions: ConversationAction[] = []

    const hasMessages = Object.values(messagesByLanguage).some((msg) => msg && msg.trim() !== '')
    if (hasMessages) {
      actions.push({
        type: 'say',
        parameters: messagesByLanguage,
      })
    }

    const { connections, usesTerminal } = _buildConnections(node.id, edges, variable)
    const humanReviewChecks = _buildHumanReviewChecksForApi(humanReviewCheck)

    if (usesTerminal) {
      needsTerminalNode = true
    }

    const isStringVariable = variable?.type === EVariableType.STRING
    if ((!variable || isStringVariable) && connections.length === 0) {
      actions.push({
        type: 'end',
        parameters: {},
      })
    }

    const isMediaNode = variable?.type === EVariableType.MEDIA

    conversationNodes[node.id] = {
      ui: {
        xPos: node.position.x,
        yPos: node.position.y,
        title,
      },
      actions,
      connections,
      ...(variable?.id && { VariableId: variable.id }),
      ...(clarificationMessage && { clarificationMessage }),
      ...(humanReviewChecks && { humanReviewChecks }),
      // Always write the isRequired flag for MEDIA nodes so old nodes without
      // the field are explicitly saved with their current value on next edit.
      ...(isMediaNode && { isRequired: variable.isRequired !== false }),
    }
  })

  if (needsTerminalNode) {
    conversationNodes.TERMINAL = {
      ui: {
        xPos: 0,
        yPos: 0,
        title: 'Terminal',
      },
      actions: [
        {
          type: 'end',
          parameters: {},
        },
      ],
      connections: [],
    }
  }

  return conversationNodes
}

function _buildConnections(
  nodeId: string,
  edges: Edge[],
  variable?: TNodeVariable,
): { connections: ConversationConnection[]; usesTerminal: boolean } {
  const connections: ConversationConnection[] = []
  let usesTerminal = false

  const nodeEdges = edges.filter((edge) => edge.source === nodeId)

  if (!variable) {
    nodeEdges.forEach((edge) => {
      connections.push({
        type: 'always',
        parameters: {},
        target: edge.target,
      })
    })
    return { connections, usesTerminal }
  }

  if (variable.type === EVariableType.BOOLEAN) {
    const yesEdge = nodeEdges.find((edge) => edge.sourceHandle === 'yes')
    const noEdge = nodeEdges.find((edge) => edge.sourceHandle === 'no')

    if (yesEdge) {
      connections.push({
        type: 'onVariableValue',
        parameters: {
          booleanValue: true,
        },
        target: yesEdge.target,
        actions: _buildActionsFromCondition(variable.condition?.yes),
      })
    } else if (variable.condition?.yes && _hasActions(variable.condition.yes)) {
      connections.push({
        type: 'onVariableValue',
        parameters: {
          booleanValue: true,
        },
        target: 'TERMINAL',
        actions: _buildActionsFromCondition(variable.condition.yes),
      })
      usesTerminal = true
    }

    if (noEdge) {
      connections.push({
        type: 'onVariableValue',
        parameters: {
          booleanValue: false,
        },
        target: noEdge.target,
        actions: _buildActionsFromCondition(variable.condition?.no),
      })
    } else if (variable.condition?.no && _hasActions(variable.condition.no)) {
      connections.push({
        type: 'onVariableValue',
        parameters: {
          booleanValue: false,
        },
        target: 'TERMINAL',
        actions: _buildActionsFromCondition(variable.condition.no),
      })
      usesTerminal = true
    }
  }

  if (variable.type === EVariableType.INTEGER_VALUE_RANGE && variable.condition?.ranges) {
    // Filter out invalid ranges (incomplete ranges with NaN upperBound)
    const validRanges = variable.condition.ranges.filter(
      (range) => !Number.isNaN(range.lowerBound) && !Number.isNaN(range.upperBound),
    )

    validRanges.forEach((range) => {
      // Find edge using original index from all ranges
      const originalIndex = variable.condition!.ranges.indexOf(range)
      const rangeEdge = nodeEdges.find((edge) => edge.sourceHandle === `range-${originalIndex}`)

      const integerValue =
        range.lowerBound === range.upperBound
          ? { exact: range.lowerBound }
          : { min: range.lowerBound, max: range.upperBound }

      if (rangeEdge) {
        connections.push({
          type: 'onVariableValue',
          parameters: {
            integerValue,
          },
          target: rangeEdge.target,
          actions: _buildActionsFromCondition(range.action),
        })
      } else {
        // Include all ranges in the payload, even those without actions
        connections.push({
          type: 'onVariableValue',
          parameters: {
            integerValue,
          },
          target: 'TERMINAL',
          actions: _buildActionsFromCondition(range.action),
        })
        usesTerminal = true
      }
    })
  }

  if (variable.type === EVariableType.MEDIA) {
    const uploadedEdge = nodeEdges.find((edge) => edge.sourceHandle === MEDIA_UPLOADED_HANDLE)

    if (uploadedEdge) {
      connections.push({
        type: 'onVariableValue',
        parameters: { mediaUploaded: true },
        target: uploadedEdge.target,
      })
    } else {
      connections.push({
        type: 'onVariableValue',
        parameters: { mediaUploaded: true },
        target: 'TERMINAL',
      })
      usesTerminal = true
    }

    // "Document not received" only exists on optional documents. Without a drawn edge nothing is
    // emitted, so the conversation keeps waiting instead of silently ending.
    const notUploadedEdge = nodeEdges.find((edge) => edge.sourceHandle === MEDIA_NOT_UPLOADED_HANDLE)
    if (variable.isRequired === false && notUploadedEdge) {
      connections.push({
        type: 'onVariableValue',
        parameters: { mediaUploaded: false },
        target: notUploadedEdge.target,
      })
    }
  }

  if (variable.type === EVariableType.STRING) {
    nodeEdges.forEach((edge) => {
      connections.push({ type: 'onVariableValue', parameters: { stringCaptured: true }, target: edge.target })
    })
  }

  return { connections, usesTerminal }
}

function _hasActions(conditionAction: TNodeAction): boolean {
  return !!(conditionAction.medAlertId || conditionAction.requestHumanReviewProblem)
}

function _buildActionsFromCondition(conditionAction?: TNodeAction): ConversationAction[] | undefined {
  if (!conditionAction) return undefined

  const actions: ConversationAction[] = []

  if (conditionAction.medAlertId) {
    actions.push({
      type: 'setMedAlert',
      parameters: {
        MedAlertId: conditionAction.medAlertId,
      },
    })
  }

  if (conditionAction.requestHumanReviewProblem) {
    actions.push({
      type: 'requestHumanReview',
      parameters: { problem: conditionAction.requestHumanReviewProblem },
    })
  }

  return actions.length > 0 ? actions : undefined
}

function _createBooleanVariable(
  variableId: number,
  connections: ConversationConnection[],
): TNodeVariable | undefined {
  const booleanConnections = connections.filter(
    (conn) => conn.type === 'onVariableValue' && 'booleanValue' in conn.parameters,
  )

  if (booleanConnections.length > 0) {
    const yesConnection = booleanConnections.find((conn) => conn.parameters.booleanValue === true)
    const noConnection = booleanConnections.find((conn) => conn.parameters.booleanValue === false)

    return {
      id: variableId,
      type: EVariableType.BOOLEAN,
      condition: {
        yes: _extractActionFromConnection(yesConnection),
        no: _extractActionFromConnection(noConnection),
      },
    }
  }

  return {
    id: variableId,
    type: EVariableType.BOOLEAN,
  }
}

function _createIntegerRangeVariable(
  variableId: number,
  connections: ConversationConnection[],
  projectVariable: TVariable,
): TNodeVariable | undefined {
  const integerConnections = connections.filter(
    (conn) => conn.type === 'onVariableValue' && 'integerValue' in conn.parameters,
  )

  const minValue = projectVariable.minValue ?? 0
  const maxValue = projectVariable.maxValue ?? 0

  if (integerConnections.length > 0) {
    const ranges: TRange[] = integerConnections
      .map((conn) => {
        const intValue = conn.parameters.integerValue
        const lowerBound = intValue?.exact ?? intValue?.min ?? 0
        const upperBound = intValue?.exact ?? intValue?.max ?? 0

        return {
          lowerBound,
          upperBound,
          action: _extractActionFromConnection(conn),
        }
      })
      .sort((a, b) => a.lowerBound - b.lowerBound)

    // Check if we need to add a default incomplete range
    const lastRange = ranges.at(-1)
    const needsIncompleteRange =
      lastRange && !Number.isNaN(lastRange.upperBound) && lastRange.upperBound < maxValue

    if (needsIncompleteRange) {
      ranges.push({
        lowerBound: lastRange.upperBound + 1,
        upperBound: Number.NaN,
        action: {},
      })
    }

    return {
      id: variableId,
      type: EVariableType.INTEGER_VALUE_RANGE,
      minValue,
      maxValue,
      condition: {
        ranges,
      },
    }
  }

  // No ranges from API: initialize with default range from minValue to NaN
  return {
    id: variableId,
    type: EVariableType.INTEGER_VALUE_RANGE,
    minValue,
    maxValue,
    condition: {
      ranges: [
        {
          lowerBound: minValue,
          upperBound: Number.NaN,
          action: {},
        },
      ],
    },
  }
}

// Backward-compat: extract a STRING variable from a legacy capture node (2-node pattern).
function _extractStringVariableFromCaptureNode(
  captureNodeDef: ConversationNodes[string],
  projectVariables: TVariable[],
): TNodeVariable | undefined {
  const setVariableAction = captureNodeDef.actions.find(
    (action) => action.type === 'setVariable' && action.parameters?.value === '${conversation.input}',
  )
  if (!setVariableAction) {
    return undefined
  }

  const variableName = setVariableAction.parameters?.variableName
  const projectVariable = projectVariables.find(
    (variable) => variable.name === variableName && variable.type === EVariableType.STRING,
  )
  if (!projectVariable) {
    return undefined
  }

  return {
    id: projectVariable.id,
    type: EVariableType.STRING,
  }
}

function _extractVariable(
  nodeDefinition: ConversationNodes[string],
  projectVariables: TVariable[],
): TNodeVariable | undefined {
  if (!nodeDefinition.VariableId) {
    return undefined
  }

  const projectVariable = projectVariables.find((v) => v.id === nodeDefinition.VariableId)
  if (!projectVariable) {
    return undefined
  }

  if (projectVariable.type === EVariableType.BOOLEAN) {
    return _createBooleanVariable(nodeDefinition.VariableId, nodeDefinition.connections)
  }

  if (projectVariable.type === EVariableType.INTEGER_VALUE_RANGE) {
    return _createIntegerRangeVariable(nodeDefinition.VariableId, nodeDefinition.connections, projectVariable)
  }

  if (projectVariable.type === EVariableType.MEDIA) {
    // Default to mandatory (isRequired: true) for nodes that predate this field.
    const isRequired = nodeDefinition.isRequired !== false
    return {
      id: nodeDefinition.VariableId,
      type: EVariableType.MEDIA,
      isRequired,
    }
  }

  if (projectVariable.type === EVariableType.STRING) {
    return {
      id: nodeDefinition.VariableId,
      type: EVariableType.STRING,
    }
  }

  return undefined
}

function _determineSourceHandle(
  connection: ConversationConnection,
  variable?: TNodeVariable,
): string | undefined {
  if (connection.type !== 'onVariableValue') {
    return undefined
  }

  if ('booleanValue' in connection.parameters) {
    return connection.parameters.booleanValue ? 'yes' : 'no'
  }

  if ('integerValue' in connection.parameters && variable?.type === EVariableType.INTEGER_VALUE_RANGE) {
    const rangeIndex = variable.condition?.ranges.findIndex((range) => {
      const intValue = connection.parameters.integerValue
      if (intValue?.exact !== undefined) {
        return range.lowerBound === intValue.exact && range.upperBound === intValue.exact
      }
      return range.lowerBound === intValue?.min && range.upperBound === intValue?.max
    })

    return rangeIndex !== undefined && rangeIndex >= 0 ? `range-${rangeIndex}` : undefined
  }

  if ('mediaUploaded' in connection.parameters && connection.parameters.mediaUploaded === true) {
    return MEDIA_UPLOADED_HANDLE
  }

  if (
    'mediaUploaded' in connection.parameters &&
    variable?.type === EVariableType.MEDIA &&
    variable.isRequired === false
  ) {
    return MEDIA_NOT_UPLOADED_HANDLE
  }

  return undefined
}

function _createEdgesFromConnections(
  nodeId: string,
  connections: ConversationConnection[],
  variable: TNodeVariable | undefined,
  edgeIdCounter: { value: number },
): Edge[] {
  return connections
    .filter((connection) => connection.target !== 'TERMINAL')
    .flatMap((connection) => {
      const sourceHandle = _determineSourceHandle(connection, variable)

      // If the connection type is onVariableValue but no sourceHandle could be determined,
      // it is an unsupported or removed branch (e.g. mediaUploaded: false on a mandatory document) — skip it.
      // Exception: stringCaptured connections are valid but have no named handle.
      if (
        connection.type === 'onVariableValue' &&
        sourceHandle === undefined &&
        !('stringCaptured' in connection.parameters)
      ) {
        return []
      }

      return [
        {
          id: `edge-${edgeIdCounter.value++}`,
          source: nodeId,
          sourceHandle,
          target: connection.target,
          type: DEFAULT_EDGE_TYPE,
        },
      ]
    })
}

export function convertApiNodesToReactFlow(
  conversationNodes: ConversationNodes,
  projectVariables: TVariable[],
): {
  nodes: TChatbotNode[]
  edges: Edge[]
} {
  const nodes: TChatbotNode[] = []
  const edges: Edge[] = []
  const edgeIdCounter = { value: 0 }

  // Backward-compat pre-pass: identify synthetic capture nodes from the old STRING 2-node
  // pattern. A capture node is the target of a `default` connection from a question node
  // and has a `setVariable` action with `${conversation.input}`.
  const captureNodeMap = new Map<string, string>() // questionNodeId → captureNodeId
  Object.entries(conversationNodes).forEach(([nodeId, nodeDef]) => {
    for (const conn of nodeDef.connections) {
      if (conn.type === 'default') {
        const captureNodeDef = conversationNodes[conn.target]
        if (
          captureNodeDef?.actions.some(
            (action) => action.type === 'setVariable' && action.parameters?.value === '${conversation.input}',
          )
        ) {
          captureNodeMap.set(nodeId, conn.target)
          break
        }
      }
    }
  })
  const captureNodeIds = new Set(captureNodeMap.values())

  Object.entries(conversationNodes).forEach(([nodeId, nodeDefinition]) => {
    if (nodeId === 'TERMINAL' || captureNodeIds.has(nodeId)) {
      return
    }

    const isStartNode = nodeId === START_NODE_ID

    const sayAction = nodeDefinition.actions.find((action) => action.type === 'say')
    const messagesByLanguage: Partial<Record<TPatientLocale, string>> = sayAction?.parameters || {}

    const captureNodeId = captureNodeMap.get(nodeId)
    const captureNodeDef = captureNodeId ? conversationNodes[captureNodeId] : undefined

    // For old 2-node STRING nodes: extract variable from the capture node; edges come from
    // the capture node's connections (which point to the actual next node).
    const variable = captureNodeDef
      ? _extractStringVariableFromCaptureNode(captureNodeDef, projectVariables)
      : _extractVariable(nodeDefinition, projectVariables)

    const connectionsForEdges = captureNodeDef ? captureNodeDef.connections : nodeDefinition.connections

    const node: TChatbotNode = {
      id: nodeId,
      type: 'chatbotMessage',
      position: {
        x: nodeDefinition.ui?.xPos ?? 250,
        y: nodeDefinition.ui?.yPos ?? 100,
      },
      data: {
        title: nodeDefinition.ui?.title || '',
        messagesByLanguage,
        variable,
        clarificationMessage: captureNodeDef ? undefined : nodeDefinition.clarificationMessage,
        isEditable: !isStartNode,
        humanReviewCheck: _extractHumanReviewCheckFromApi(nodeDefinition.humanReviewChecks),
      },
      ...(isStartNode && {
        selectable: false,
        deletable: false,
      }),
    }

    nodes.push(node)

    const nodeEdges = _createEdgesFromConnections(nodeId, connectionsForEdges, variable, edgeIdCounter)
    edges.push(...nodeEdges)
  })

  return { nodes, edges }
}

function _extractActionFromConnection(connection?: ConversationConnection): TNodeAction {
  const action: TNodeAction = {}

  if (!connection?.actions) return action

  connection.actions.forEach((act) => {
    if (act.type === 'setMedAlert' && act.parameters.MedAlertId) {
      action.medAlertId = act.parameters.MedAlertId
    }
    if (act.type === 'requestHumanReview') {
      if (act.parameters.problem) {
        action.requestHumanReviewProblem = isHumanReviewRequestProblem(act.parameters.problem)
          ? act.parameters.problem
          : EHumanReviewRequestProblem.UNKNOWN
      } else {
        action.requestHumanReviewProblem = EHumanReviewRequestProblem.DECLARED_COMPLICATION
      }
    }
  })

  return action
}
